import React, {
  useEffect, useLayoutEffect, useMemo, useState,
} from 'react';
import * as S from './ActivitySelector.styled';
import { moveTmpPanel } from 'pages/Navigation/utils';
import Search from '@mui/icons-material/Search';
import Close from '@mui/icons-material/Close';
import Check from '@mui/icons-material/Check';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import Logout from '@mui/icons-material/Logout';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useLocation, useHistory } from 'react-router-dom';
import { isActivityLogsPagePath } from 'pages/Layout/erpModulePaths';
import {
  ACTIVITY_LOGS_LABEL_LONG,
  ACTIVITY_LOGS_LABEL_MEDIUM,
} from '../activityLogLabels';
import {
  DEFAULT_LOG_SEARCH_OPTIONS,
  LogSearchOptions,
  selectLogSearch,
  selectLogSearchOptions,
  selectLogSearches,
  selectLogsChecked,
  selectActivityLogsCombinedChecked,
  selectLogsValueMode,
  selectActivityLogsCombineSku,
  selectIsActivityLogsReportLogsFocus,
  selectActivityLogsOpenScopeBranchKey,
  selectActivityLogsSearchStatus,
  selectActivityLogsOnlyLogIds,
  selectActivityLogsViewAs,
  ACTIVITY_LOGS_REPORT_LOGS_FOCUS_GROUPS_TOAST,
  setLogSearch,
  setLogSearchOptions,
  setLogSearches,
  setActivityLogsCombineSku,
  setActivityLogsStartEndLogIds,
  setActivityLogsOpenScopeBranchKey,
  setActivityLogsOnlyLogIds,
  setAssociateElevatedActivityLogs,
  setComplianceActivity,
  setSingleLogTime,
} from '../redux';
import { useLogs, useLogsSelector } from '../hooks';
import {
  ActivitySkuHierarchyGroup,
  ALL_ACTIVITY_SCOPE_ID,
  SHOW_ALL_ACTIVITY_LOGS,
  activityLogsScopeTreeRoots,
  activityOccurrenceKey,
  collectActiveScopeNames,
  collectAllScopeOccurrenceKeys,
  collectScopeOccurrenceKeys,
  isOrganizationKindScopeId,
  isDepartmentPartitionScope,
  occurrenceKeyIsOn,
  occurrenceKeysCheckState,
  scopeNodeHasSkusNotOnlyChildren,
} from '../activityGroupsModel';
import { hasActiveLogSearch } from '../logSearch';
import LogSearchOptionsMenu, { chipModeIcon } from './LogSearchOptionsMenu';
import ActivityLogsDraftSearch from './ActivityLogsDraftSearch';
import ActivityLogsTableFilters from './ActivityLogsTableFilters';
import CollapsibleLeftPane from 'pages/components/CollapsibleLeftPane/CollapsibleLeftPane';
import { logRes1 } from 'pages/styles';
import { ModalParts } from 'pages/redux';
import { toast } from 'utils/toast';

const MAX_ACTIVE_SCOPE_PREVIEW = 5;

export const ActivitySelector = ({
  isWeb,
  useEscape: _useEscape,
  isNoSearch,
  setModal,
}:{
  isWeb?: boolean,
  /** Kept for callers; mobile close lives in the panel header now. */
  useEscape?: boolean,
  isNoSearch?: true,
  setModal?: (modal: ModalParts | null) => void,
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const onActivityLogsPage = isActivityLogsPagePath(location.pathname);
  const checked = useAppSelector(selectLogsChecked);
  const combinedChecked = useAppSelector(selectActivityLogsCombinedChecked);
  const logSearch = useAppSelector(selectLogSearch);
  const logSearchOptions = useAppSelector(selectLogSearchOptions);
  const logSearches = useAppSelector(selectLogSearches);
  const logsValueMode = useAppSelector(selectLogsValueMode);
  const activityLogsCombineSku = useAppSelector(selectActivityLogsCombineSku);
  const isUnitsMode = logsValueMode === 'units';
  const {
    activityGroupsModel,
    isLoadingActivityGroups,
    runActivityLogsSearch,
  } = useLogs();
  const {
    onClickShowAll,
    onClickActivitySkuIds,
  } = useLogsSelector({});
  const activityLogsSearchStatus = useAppSelector(selectActivityLogsSearchStatus);
  // /activity stays at the shared pane width. Metrics still widens for Time/Cash labels.
  const expandWidth = !onActivityLogsPage;
  // Combine SKUs only when /logs shows results — hide during the right-pane search prompt/loader.
  const showCombineSku = !onActivityLogsPage || activityLogsSearchStatus === 'results';
  // /logs: draft search + Time/Cash live in the right pane; left pane keeps saved chips only.
  const showDraftAndFiltersInPane = !onActivityLogsPage || activityLogsSearchStatus === 'loading';
  // Shared drill path — Scopes and SKU show the same departments→… state.
  const openSkuBranchKey = useAppSelector(selectActivityLogsOpenScopeBranchKey);
  const [paneListSearch, setPaneListSearch] = useState('');
  // Per open dropdown — filters that node’s children / SKUs only.
  const [dropdownSearchByNodeKey, setDropdownSearchByNodeKey] = useState<
    Record<string, string>
  >({});
  const [editingChip, setEditingChip] = useState<number | null>(null);
  const isReportLogsFocus = useAppSelector(selectIsActivityLogsReportLogsFocus);
  const activityLogsOnlyLogIds = useAppSelector(selectActivityLogsOnlyLogIds);
  const activityLogsViewAs = useAppSelector(selectActivityLogsViewAs);
  const history = useHistory();

  const setOpenSkuBranchKey = (next: string | null) => {
    dispatch(setActivityLogsOpenScopeBranchKey(next));
  };

  const guardReportLogsGroupChange = (action: () => void) => {
    // Staff-report view-as stays locked until Exit.
    if (activityLogsViewAs || isReportLogsFocus) {
      toast(ACTIVITY_LOGS_REPORT_LOGS_FOCUS_GROUPS_TOAST, { type: 'info' });
      return;
    }

    // Cash-session pin: drop figure pin/time so the checkbox toggle actually applies.
    if (activityLogsOnlyLogIds?.length) {
      dispatch(setActivityLogsOnlyLogIds(null));
      dispatch(setAssociateElevatedActivityLogs(false));
      dispatch(setComplianceActivity({ active: false }));
      dispatch(setSingleLogTime(null));
      dispatch(setActivityLogsStartEndLogIds(null));
      if (location.search) {
        history.replace({ pathname: location.pathname, search: '' });
      }
      action();
      void runActivityLogsSearch('search');
      return;
    }
    action();
  };

  const onActivityLogsScroll = (e: any, scrollTop?: number) => {
    const scrollT = scrollTop || e.target.scrollTop;
    localStorage.setItem('activityLogs_scroll', (scrollT || 0).toString());
  };

  const scrollActivityLogsPos = () => {
    const scrollTop = Number(localStorage.getItem('activityLogs_scroll') || 0);
    document.getElementById('activityLogs')?.scrollTo({ top: scrollTop });
  };

  useLayoutEffect(() => {
    scrollActivityLogsPos();
  }, [checked]);

  const parentSkuNodeKey = (nodeKey: string) => {
    const i = nodeKey.lastIndexOf(':');
    return i === -1 ? null : nodeKey.slice(0, i);
  };

  // True for the open node and each ancestor up the branch.
  const isSkuNodeOnBranch = (nodeKey: string) => (
    Boolean(openSkuBranchKey && (
      openSkuBranchKey === nodeKey || openSkuBranchKey.startsWith(`${nodeKey}:`)
    ))
  );

  // Keep dropdown search only for the open node and its parents.
  const pruneDropdownSearchToBranch = (branchKey: string | null) => {
    setDropdownSearchByNodeKey((searches) => {
      if (!branchKey) return {};
      const keep = new Set<string>();
      let key: string | null = branchKey;
      while (key) {
        keep.add(key);
        key = parentSkuNodeKey(key);
      }
      const next: Record<string, string> = {};
      Object.keys(searches).forEach((k) => {
        if (keep.has(k)) next[k] = searches[k];
      });
      return next;
    });
  };

  // Click-to-view: open this node, or back up to parent if already open.
  const openScopeBranch = (nodeKey: string, canDrill: boolean) => {
    if (!canDrill) return;
    const next = openSkuBranchKey === nodeKey ? parentSkuNodeKey(nodeKey) : nodeKey;
    setOpenSkuBranchKey(next);
    pruneDropdownSearchToBranch(next);
  };

  const toggleNodeCheckbox = (occurrenceKeys: string[], nodeId: string) => {
    guardReportLogsGroupChange(
      () => onClickActivitySkuIds(occurrenceKeys, checked, nodeId),
    );
  };

  // Large screen: Show all is the open tab. Mobile overlay: both tabs stay in sync.
  const showAllCheckedTokens = isWeb ? checked : combinedChecked;
  const showAllIsOn = showAllCheckedTokens.includes(SHOW_ALL_ACTIVITY_LOGS);

  const hasAnySearch = hasActiveLogSearch(logSearches, logSearch);

  const clearAllSearch = () => {
    dispatch(setLogSearches([]));
    dispatch(setLogSearch(''));
    dispatch(setLogSearchOptions({ ...DEFAULT_LOG_SEARCH_OPTIONS }));
  };

  const removeSearch = (index: number) => {
    // Dropping a chip may promote a later “Add from prior results” chip to first — clear that flag.
    const next = logSearches
      .filter((_, i) => i !== index)
      .map((f, i) => (
        i === 0 && f.options.fromFullLogs
          ? { ...f, options: { ...f.options, fromFullLogs: false } }
          : f
      ));
    dispatch(setLogSearches(next));
    // Draft option only applies with a prior chip.
    if (next.length === 0 && logSearchOptions.fromFullLogs) {
      dispatch(setLogSearchOptions({ ...logSearchOptions, fromFullLogs: false }));
    }
  };

  const updateChipOptions = (index: number, options: LogSearchOptions) => {
    // First chip cannot union from prior results (nothing prior to add into).
    const nextOptions = index === 0 && options.fromFullLogs
      ? { ...options, fromFullLogs: false }
      : options;
    dispatch(setLogSearches(logSearches.map((f, i) => (
      i === index ? { ...f, options: nextOptions } : f
    ))));
  };

  const updateChipTerm = (index: number, term: string) => {
    dispatch(setLogSearches(logSearches.map((f, i) => (
      i === index ? { ...f, term } : f
    ))));
  };

  const commitChipTerm = (index: number, raw: string) => {
    const term = raw.trim();
    if (!term) {
      removeSearch(index);
      return;
    }
    if (term !== logSearches[index]?.term) {
      updateChipTerm(index, term);
    }
  };

  const paneSearchTerm = paneListSearch.trim().toLowerCase();
  const allOccurrenceKeys = useMemo(
    () => collectAllScopeOccurrenceKeys(activityGroupsModel),
    [activityGroupsModel],
  );
  const activeScopeNames = useMemo(
    () => collectActiveScopeNames(
      activityGroupsModel.skuHierarchy || [],
      checked,
      allOccurrenceKeys,
      true,
    ),
    [activityGroupsModel.skuHierarchy, checked, allOccurrenceKeys],
  );
  const visibleActiveScopeNames = activeScopeNames.slice(0, MAX_ACTIVE_SCOPE_PREVIEW);
  const extraActiveScopeCount = Math.max(
    0,
    activeScopeNames.length - MAX_ACTIVE_SCOPE_PREVIEW,
  );

  const visibleChildScopes = (
    node: ActivitySkuHierarchyGroup,
  ) => (
    (node.children || []).filter((c) => {
      if (!c.isObjectGroup) return true;
      return isDepartmentPartitionScope(node.id, activityGroupsModel);
    })
  );

  const skuNodeMatchesPaneSearch = (node: ActivitySkuHierarchyGroup): boolean => {
    if (!paneSearchTerm) return true;
    if (node.name.toLowerCase().includes(paneSearchTerm)) return true;
    if ((node.skus || []).some((s) => s.name.toLowerCase().includes(paneSearchTerm))) {
      return true;
    }
    if ((node.groupsVoRefs || []).some((s) => s.name.toLowerCase().includes(paneSearchTerm))) {
      return true;
    }
    if ((node.boActionRefs || []).some((s) => s.name.toLowerCase().includes(paneSearchTerm))) {
      return true;
    }
    // Walk VO/BO too — Scopes hides them in the list but search must reach their SKUs.
    return (node.children || []).some(skuNodeMatchesPaneSearch);
  };

  // Same walk as pane search, for the open dropdown’s own field.
  const nodeMatchesDropdownSearch = (
    node: ActivitySkuHierarchyGroup,
    term: string,
  ): boolean => {
    if (!term) return true;
    if (node.name.toLowerCase().includes(term)) return true;
    if ((node.skus || []).some((s) => s.name.toLowerCase().includes(term))) {
      return true;
    }
    return (node.children || []).some((child) => nodeMatchesDropdownSearch(child, term));
  };

  const renderSkuLeaf = (
    sku: { id: string, name: string },
    keyPrefix: string,
    parentPath: string[],
    scopesSurface: 'white' | 'active',
  ) => {
    const skuKey = activityOccurrenceKey(parentPath, sku.id);
    const isSelected = occurrenceKeyIsOn(skuKey, checked, allOccurrenceKeys);
    return (
      <S.ActivityName
        key={`${keyPrefix}_${sku.id}`}
        isSelected={isSelected}
        $scopesSurface={scopesSurface}
        onClick={(e) => {
          e.stopPropagation();
          guardReportLogsGroupChange(
            () => onClickActivitySkuIds([skuKey], checked, sku.id),
          );
        }}
      >
        <S.CheckBox
          $state={isSelected ? 'on' : 'off'}
          $clickable
          role="checkbox"
          aria-checked={isSelected}
          aria-label={`Select ${sku.name}`}
          onClick={(e) => {
            e.stopPropagation();
            guardReportLogsGroupChange(
              () => onClickActivitySkuIds([skuKey], checked, sku.id),
            );
          }}
        >
          {isSelected ? <Check fontSize="inherit" /> : null}
        </S.CheckBox>
        <S.ActivityNameText>{sku.name}</S.ActivityNameText>
      </S.ActivityName>
    );
  };

  const renderTreeNode = (
    node: ActivitySkuHierarchyGroup,
    keyPrefix: string,
    depth: number,
    parentPath: string[],
  ): React.ReactNode => {
    if (node.isObjectGroup) {
      const onDepartmentPartition = parentPath.some(
        (id) => isDepartmentPartitionScope(id, activityGroupsModel),
      );
      if (!onDepartmentPartition) return null;
    }

    const isAllActivityScope = node.id === ALL_ACTIVITY_SCOPE_ID;
    const isOrgScopeNode = isOrganizationKindScopeId(node.id, activityGroupsModel);
    const isSyntheticScope = isAllActivityScope || isOrgScopeNode;
    const isCurrentOrgNode = isOrgScopeNode;
    const nodeKey = `${keyPrefix}:${node.id}`;
    const childGroups = visibleChildScopes(node);
    const hasChildGroups = childGroups.length > 0;
    const useBlueScopeLeaf = scopeNodeHasSkusNotOnlyChildren(node);
    const showGroupsVoRefs = node.isDepartmentPartitionGroup;
    const showGroupsBoRefs = node.isDepartmentBoAggregate;
    const nodeNameMatchesPane = Boolean(paneSearchTerm)
      && node.name.toLowerCase().includes(paneSearchTerm);
    const paneOpensNode = Boolean(paneSearchTerm) && !nodeNameMatchesPane && (
      childGroups.some(skuNodeMatchesPaneSearch)
      || (node.children || []).some(skuNodeMatchesPaneSearch)
      || (node.skus || []).some((s) => s.name.toLowerCase().includes(paneSearchTerm))
    );
    // Shared path display: only the open branch + its children (both tabs).
    const onOpenBranch = Boolean(openSkuBranchKey) && isSkuNodeOnBranch(nodeKey);
    const childOfOpen = Boolean(openSkuBranchKey)
      && parentSkuNodeKey(nodeKey) === openSkuBranchKey;
    // Hide siblings off the drill path (unless pane search is filtering).
    if (openSkuBranchKey && !paneSearchTerm) {
      if (!onOpenBranch && !childOfOpen) return null;
    }
    // Org scope is the tree root — keep it open like the old synthetic Organization row.
    const isExpanded = (
      (isOrgScopeNode && depth === 0 && !openSkuBranchKey)
      || (isAllActivityScope && !openSkuBranchKey)
      || (!useBlueScopeLeaf && (onOpenBranch || paneOpensNode))
    );
    const occurrenceKeys = isSyntheticScope
      ? (
        (isAllActivityScope || isCurrentOrgNode)
          ? collectAllScopeOccurrenceKeys(activityGroupsModel)
          : []
      )
      : collectScopeOccurrenceKeys(node, parentPath);
    const checkState = occurrenceKeysCheckState(occurrenceKeys, checked, allOccurrenceKeys);
    let visibleChildren = childGroups;
    if (paneSearchTerm && !nodeNameMatchesPane) {
      visibleChildren = visibleChildren.filter(skuNodeMatchesPaneSearch);
    }
    const canExpand = hasChildGroups || showGroupsVoRefs || showGroupsBoRefs;
    // Active drill row vs ancestors still on the path (siblings hidden).
    const isActiveDrillParent = openSkuBranchKey === nodeKey;
    const localSearchTerm = isActiveDrillParent
      ? (dropdownSearchByNodeKey[nodeKey] || '').trim().toLowerCase()
      : '';
    let visibleGroupsVoRefs = node.groupsVoRefs || [];
    let visibleGroupsBoRefs = node.boActionRefs || [];
    if (paneSearchTerm && !nodeNameMatchesPane && showGroupsVoRefs) {
      visibleGroupsVoRefs = visibleGroupsVoRefs.filter(
        (ref) => ref.name.toLowerCase().includes(paneSearchTerm),
      );
    }
    if (paneSearchTerm && !nodeNameMatchesPane && showGroupsBoRefs) {
      visibleGroupsBoRefs = visibleGroupsBoRefs.filter(
        (ref) => ref.name.toLowerCase().includes(paneSearchTerm),
      );
    }
    if (localSearchTerm && showGroupsVoRefs) {
      visibleGroupsVoRefs = visibleGroupsVoRefs.filter(
        (ref) => ref.name.toLowerCase().includes(localSearchTerm),
      );
    }
    if (localSearchTerm && showGroupsBoRefs) {
      visibleGroupsBoRefs = visibleGroupsBoRefs.filter(
        (ref) => ref.name.toLowerCase().includes(localSearchTerm),
      );
    }
    if (localSearchTerm) {
      visibleChildren = visibleChildren.filter(
        (child) => nodeMatchesDropdownSearch(child, localSearchTerm),
      );
    }
    const showDropdownSearch = isActiveDrillParent && canExpand;
    const dropdownSearchMiss = Boolean(localSearchTerm)
      && visibleChildren.length === 0
      && visibleGroupsVoRefs.length === 0
      && visibleGroupsBoRefs.length === 0;
    const isAncestorDrillParent = Boolean(
      openSkuBranchKey && onOpenBranch && !isActiveDrillParent,
    );
    const drillIconMode: 'down' | 'up' | 'exit' | null = !canExpand
      ? null
      : isActiveDrillParent
        ? 'exit'
        : isAncestorDrillParent
          ? 'up'
          : 'down';
    // Path parents (and undrilled roots) white; children under a white parent light grey.
    const scopesSurface: 'white' | 'active' = (onOpenBranch || depth === 0)
      ? 'white'
      : 'active';

    // Include this node in the path so org/parent toggles match descendant checkbox keys.
    const childParentPath = [...parentPath, node.id];

    if (useBlueScopeLeaf) {
      const isTypeSelected = checkState === 'on';
      return (
        <S.ActivityName
          key={nodeKey}
          isSelected={isTypeSelected}
          $scopesSurface={scopesSurface}
          // Leaf scope: row click toggles the same as the checkbox.
          onClick={(e) => {
            e.stopPropagation();
            toggleNodeCheckbox(occurrenceKeys, node.id);
          }}
        >
          <S.CheckBox
            $state={checkState === 'on' ? 'on' : 'off'}
            $clickable
            role="checkbox"
            aria-checked={isTypeSelected}
            aria-label={`Select ${node.name}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleNodeCheckbox(occurrenceKeys, node.id);
            }}
          >
            {isTypeSelected ? <Check fontSize="inherit" /> : null}
          </S.CheckBox>
          <S.ActivityNameText>{node.name}</S.ActivityNameText>
        </S.ActivityName>
      );
    }

    return (
      <S.ActivityCont key={nodeKey} isCollapsed={!isExpanded} $compactBottom>
        <S.ActivityGroup
          $scopesSurface={scopesSurface}
          onClick={(e) => {
            e.stopPropagation();
            // Same click-to-view as Scopes (including VO/BO and SKU leaves).
            if (canExpand) {
              openScopeBranch(nodeKey, true);
              return;
            }
            toggleNodeCheckbox(occurrenceKeys, node.id);
          }}
        >
          <S.CheckBox
            $state={checkState}
            $clickable
            role="checkbox"
            aria-checked={checkState === 'on' ? true : checkState === 'partial' ? 'mixed' : false}
            aria-label={`Select ${node.name}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleNodeCheckbox(occurrenceKeys, node.id);
            }}
          >
            {checkState === 'on' ? <Check fontSize="inherit" /> : null}
          </S.CheckBox>
          {drillIconMode ? (
            <S.ScopeDrillIcon $mode={drillIconMode} aria-hidden>
              {drillIconMode === 'exit' ? (
                // Left-pointing exit — back out of this scope’s children.
                <Logout fontSize="inherit" sx={{ transform: 'scaleX(-1)' }} />
              ) : drillIconMode === 'up' ? (
                <ExpandLess fontSize="inherit" />
              ) : (
                <ExpandMore fontSize="inherit" />
              )}
            </S.ScopeDrillIcon>
          ) : null}
          <S.ActivityGroupLabel>{node.name}</S.ActivityGroupLabel>
        </S.ActivityGroup>
        {isExpanded && canExpand ? (
          <S.ActivityGroupChildren
            $symmetricActivePad={isActiveDrillParent}
          >
            {showDropdownSearch ? (
              <S.GroupTypeSearch
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Search fontSize="inherit" />
                <S.GroupTypeSearchInput
                  value={dropdownSearchByNodeKey[nodeKey] || ''}
                  placeholder={`Search ${node.name}`}
                  aria-label={`Search ${node.name}`}
                  onChange={(e) => setDropdownSearchByNodeKey((prev) => ({
                    ...prev,
                    [nodeKey]: e.target.value,
                  }))}
                />
              </S.GroupTypeSearch>
            ) : null}
            {hasChildGroups
              ? visibleChildren.map((child) => (
                renderTreeNode(child, nodeKey, depth + 1, childParentPath)
              ))
              : null}
            {showGroupsVoRefs
              ? visibleGroupsVoRefs.map((masterRef) => renderSkuLeaf(
                masterRef,
                nodeKey,
                childParentPath,
                'active',
              ))
              : null}
            {showGroupsBoRefs
              ? visibleGroupsBoRefs.map((boRef) => renderSkuLeaf(
                boRef,
                nodeKey,
                childParentPath,
                'active',
              ))
              : null}
            {showGroupsVoRefs && !visibleGroupsVoRefs.length ? (
              <S.EmptyGroupsHint>No master data in this department yet.</S.EmptyGroupsHint>
            ) : null}
            {showGroupsBoRefs && !visibleGroupsBoRefs.length ? (
              <S.EmptyGroupsHint>No event types in this department yet.</S.EmptyGroupsHint>
            ) : null}
            {dropdownSearchMiss ? (
              <S.EmptyGroupsHint>No matching activity scopes.</S.EmptyGroupsHint>
            ) : null}
          </S.ActivityGroupChildren>
        ) : null}
      </S.ActivityCont>
    );
  };

  const hierarchyRoots = activityLogsScopeTreeRoots(activityGroupsModel)
    .filter(skuNodeMatchesPaneSearch);

  // Report Show logs: keep the pane open; activity scopes toast instead of updating.
  return (
    <CollapsibleLeftPane
      isWeb={isWeb}
      title={ACTIVITY_LOGS_LABEL_LONG}
      hideBelow={logRes1}
      expandWidth={expandWidth}
      overlay={!isWeb ? (
        <S.MobilePanelHeader>
          <S.MobilePanelTitle>{ACTIVITY_LOGS_LABEL_MEDIUM}</S.MobilePanelTitle>
          <S.MobilePanelClose
            type="button"
            aria-label="Close activity logs"
            onClick={() => moveTmpPanel()}
          >
            <Close fontSize="small" />
          </S.MobilePanelClose>
        </S.MobilePanelHeader>
      ) : null}
    >
        <S.ActivityLogs id="activityLogs" onScroll={onActivityLogsScroll}>
        {!isNoSearch && (logSearches.length > 0 || showDraftAndFiltersInPane) ? (
          <S.SearchCont $belowPanelTitle $inScroll>
              {logSearches.length > 0 && (
                <S.SearchChips>
                  {logSearches.map((filter, i) => {
                    const isEditing = editingChip === i;
                    const solidNegate = Boolean(filter.options.negate && isEditing);
                    return (
                      <S.SearchChip
                        key={`activity_search_${i}`}
                        $negate={filter.options.negate}
                        $active={isEditing}
                      >
                        <S.SearchChipIcon>
                          {chipModeIcon(filter.options)}
                        </S.SearchChipIcon>
                        <S.SearchChipInput
                          value={filter.term}
                          title={filter.term}
                          aria-label={`Edit saved search ${i + 1}`}
                          placeholder="Search term…"
                          onFocus={() => setEditingChip(i)}
                          onChange={(e) => updateChipTerm(i, e.target.value)}
                          onBlur={(e) => {
                            setEditingChip(null);
                            commitChipTerm(i, e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              (e.target as HTMLInputElement).blur();
                            }
                          }}
                        />
                        <LogSearchOptionsMenu
                          compact
                          options={filter.options}
                          onChange={(next) => updateChipOptions(i, next)}
                          canClearAll={hasAnySearch}
                          onClearAll={clearAllSearch}
                          allowFromFullLogs={i > 0}
                        />
                        <S.SearchChipRemove
                          type="button"
                          $solid={solidNegate}
                          aria-label={`Remove search “${filter.term}”`}
                          onClick={() => removeSearch(i)}
                        >
                          <Close fontSize="inherit" />
                        </S.SearchChipRemove>
                      </S.SearchChip>
                    );
                  })}
                </S.SearchChips>
              )}
              {showDraftAndFiltersInPane ? (
                <ActivityLogsDraftSearch />
              ) : null}
          </S.SearchCont>
        ) : null}
        {setModal && !isNoSearch && showDraftAndFiltersInPane ? (
          <ActivityLogsTableFilters setModal={setModal} widePane={expandWidth} />
        ) : null}
        {!isUnitsMode && !isNoSearch && showCombineSku ? (
          <S.CombineSkuToggle
            type="button"
            title="Roll up rows with cash values by log type (SKU). Empty or N/A values stay separate."
            onClick={() => {
              const nextOn = !activityLogsCombineSku;
              dispatch(setActivityLogsCombineSku(nextOn));
              // Combine SKUs cannot pin a single activity log — drop the lock.
              if (nextOn) dispatch(setActivityLogsStartEndLogIds(null));
            }}
          >
            <S.CombineSkuCheck
              type="checkbox"
              checked={activityLogsCombineSku}
              readOnly
              tabIndex={-1}
            />
            Combine SKUs
          </S.CombineSkuToggle>
        ) : null}
        <S.ActivityCont $compactBottom>
          <S.ActivityGroup1
            $selected={showAllIsOn}
            onClick={() => guardReportLogsGroupChange(() => onClickShowAll(
              showAllCheckedTokens,
              Boolean(isWeb),
            ))}
          >
            <S.Select
              type="checkbox"
              checked={showAllIsOn}
              readOnly
            />
            <S.ActivityGroupLabel>Show all</S.ActivityGroupLabel>
          </S.ActivityGroup1>
        </S.ActivityCont>
        {activityGroupsModel.groups.length > 0 ? (
          <S.GroupTypeSearch>
            <Search fontSize="inherit" />
            <S.GroupTypeSearchInput
              value={paneListSearch}
              placeholder="Search activity scopes"
              aria-label="Search activity scopes"
              onChange={(e) => setPaneListSearch(e.target.value)}
            />
          </S.GroupTypeSearch>
        ) : null}
        {isLoadingActivityGroups && !activityGroupsModel.groups.length ? (
          <S.EmptyGroupsHint>Loading activity scopes…</S.EmptyGroupsHint>
        ) : null}
        {!isLoadingActivityGroups && !activityGroupsModel.groups.length ? (
          <S.EmptyGroupsHint>
            No activity scopes loaded. Soft-refresh the page, or confirm you are on the seeded organization.
          </S.EmptyGroupsHint>
        ) : null}
        {hierarchyRoots.map((root) => renderTreeNode(root, 'tree', 0, []))}
        {paneSearchTerm && !hierarchyRoots.length ? (
          <S.EmptyGroupsHint>No matching activity scopes.</S.EmptyGroupsHint>
        ) : null}
        {visibleActiveScopeNames.length > 0 ? (
          <S.ActiveScopesPreview>
            {visibleActiveScopeNames.map((name) => (
              <S.ActiveScopeName key={name} title={name}>{name}</S.ActiveScopeName>
            ))}
            {extraActiveScopeCount > 0 ? (
              <S.ActiveScopesMore>{`+${extraActiveScopeCount} more`}</S.ActiveScopesMore>
            ) : null}
          </S.ActiveScopesPreview>
        ) : null}
        </S.ActivityLogs>
    </CollapsibleLeftPane>
  );
};
